import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BellOff, CheckCircle, AlertTriangle, XCircle, Info, X, Check } from 'lucide-react';

const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol-400/70';
const SOFT_SHADOW =
  'shadow-[0_1px_2px_rgba(62,39,35,0.05),0_10px_24px_-14px_rgba(62,39,35,0.18)]';

const TYPE_STYLES = {
  success: { icon: CheckCircle, tile: 'bg-green-50 text-green-700' },
  warning: { icon: AlertTriangle, tile: 'bg-amber-50 text-amber-700' },
  error: { icon: XCircle, tile: 'bg-red-50 text-red-700' },
  info: { icon: Info, tile: 'bg-cafe-100/60 text-cafe-700' }
};

const DAY_MS = 24 * 60 * 60 * 1000;
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

// "Hoy", "Ayer" o la fecha (con año solo si no es el actual). Sin fecha válida: "Anteriores".
const getGroupLabel = (timestamp) => {
  const time = Date.parse(timestamp);
  if (Number.isNaN(time)) return 'Anteriores';

  const date = new Date(time);
  const now = new Date();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS);

  if (diffDays <= 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';

  return new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'long',
    ...(date.getFullYear() !== now.getFullYear() && { year: 'numeric' })
  }).format(date);
};

const formatTime = (timestamp) => {
  const time = Date.parse(timestamp);
  if (Number.isNaN(time)) return '';
  return new Date(time).toLocaleTimeString('es-HN', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const NotificationsModal = ({
  show,
  onClose,
  notifications = [],
  onClearAll,
  onMarkAsRead
}) => {
  const [filter, setFilter] = useState('all');
  const closeButtonRef = useRef(null);

  // PortalMain se vuelve a renderizar cada segundo (reloj) y pasa un onClose nuevo cada vez:
  // se guarda en una referencia para que el efecto no se reinicie ni le quite el foco al usuario.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!show) return;

    setFilter('all');
    closeButtonRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [show]);

  const unreadCount = notifications.filter((notification) => !notification.read).length;

  // Más recientes primero, agrupadas por día
  const groups = useMemo(() => {
    const sorted = [...notifications].sort(
      (a, b) => (Date.parse(b.timestamp) || 0) - (Date.parse(a.timestamp) || 0)
    );
    const visible = filter === 'unread' ? sorted.filter((notification) => !notification.read) : sorted;

    const map = new Map();
    visible.forEach((notification) => {
      const label = getGroupLabel(notification.timestamp);
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(notification);
    });
    return [...map.entries()];
  }, [notifications, filter]);

  if (!show) return null;

  const filterOptions = [
    { id: 'all', label: 'Todas', count: notifications.length },
    { id: 'unread', label: 'Sin leer', count: unreadCount }
  ];

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 animate-fade-in bg-cafe-800/30 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="notifications-title"
        className="absolute inset-y-0 right-0 flex w-full max-w-md animate-slide-in-right flex-col bg-[#FBF8F4] shadow-[-24px_0_60px_-30px_rgba(59,39,25,0.5)]"
      >
        {/* Encabezado */}
        <header className="flex items-start justify-between gap-3 px-5 pb-4 pt-5">
          <div>
            <h2
              id="notifications-title"
              className="font-display text-2xl font-semibold tracking-tight text-cafe-700"
            >
              Notificaciones
            </h2>
            <p className="mt-0.5 text-xs text-neutral-400">
              {unreadCount === 0 ? 'Está al día' : `${unreadCount} sin leer`}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            title="Cerrar"
            aria-label="Cerrar notificaciones"
            className={`rounded-lg p-2 text-neutral-400 transition-colors hover:bg-cafe-100/50 hover:text-cafe-700 ${FOCUS}`}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* Filtro y acción global */}
        <div className="flex items-center justify-between gap-3 px-5 pb-3">
          <div className="inline-flex rounded-lg bg-cafe-100/40 p-1" role="group" aria-label="Filtrar notificaciones">
            {filterOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setFilter(option.id)}
                aria-pressed={filter === option.id}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors duration-200 ${FOCUS} ${
                  filter === option.id
                    ? 'bg-white font-medium text-cafe-800 shadow-sm'
                    : 'text-neutral-400 hover:text-cafe-700'
                }`}
              >
                {option.label}
                <span className="ml-1.5 text-xs text-neutral-300">{option.count}</span>
              </button>
            ))}
          </div>

          {unreadCount > 0 && (
            <button
              onClick={onClearAll}
              className={`rounded-md px-1 text-xs font-medium text-cafe-600 underline-offset-4 transition-colors hover:text-cafe-800 hover:underline ${FOCUS}`}
            >
              Marcar todas como leídas
            </button>
          )}
        </div>

        {/* Lista */}
        <div className="flex-1 overflow-y-auto px-5 pb-6 [scrollbar-color:#C8A88C_transparent] [scrollbar-width:thin]">
          {groups.length === 0 ? (
            <div className="py-16 text-center">
              <div
                className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white text-cafe-500 ${SOFT_SHADOW}`}
              >
                {filter === 'unread' ? <Check className="h-6 w-6" /> : <BellOff className="h-6 w-6" />}
              </div>
              <h3 className="font-display text-lg font-semibold text-cafe-700">
                {filter === 'unread' ? 'Está al día' : 'Sin notificaciones'}
              </h3>
              <p className="mt-1 text-sm text-neutral-400">
                {filter === 'unread'
                  ? 'No tiene notificaciones pendientes por leer.'
                  : 'Las notificaciones aparecerán aquí.'}
              </p>
            </div>
          ) : (
            groups.map(([label, items]) => (
              <section key={label} className="mt-5 first:mt-1">
                <h3 className="mb-2.5 text-xs font-medium text-neutral-400">{label}</h3>
                <ul className="space-y-2.5">
                  {items.map((notification) => {
                    const style = TYPE_STYLES[notification.type] || TYPE_STYLES.info;
                    const Icon = style.icon;
                    const time = formatTime(notification.timestamp);

                    return (
                      <li
                        key={notification.id}
                        className={`flex gap-3 rounded-xl p-3.5 transition-colors ${
                          notification.read ? 'bg-white/50' : `bg-white ${SOFT_SHADOW}`
                        }`}
                      >
                        <span
                          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
                            notification.read ? 'bg-neutral-50/60 text-neutral-300' : style.tile
                          }`}
                        >
                          <Icon className="h-5 w-5" strokeWidth={1.75} />
                        </span>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <h4
                              className={`line-clamp-2 text-sm leading-snug ${
                                notification.read ? 'font-medium text-neutral-400' : 'font-semibold text-cafe-800'
                              }`}
                            >
                              {notification.title}
                            </h4>
                            {!notification.read && (
                              <span
                                className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-sol-400"
                                role="img"
                                aria-label="Sin leer"
                              />
                            )}
                          </div>

                          <p
                            className={`mt-1 line-clamp-3 text-sm leading-relaxed ${
                              notification.read ? 'text-neutral-300' : 'text-neutral-500'
                            }`}
                          >
                            {notification.message}
                          </p>

                          <div className="mt-2.5 flex items-center justify-between gap-2">
                            <p className="min-w-0 truncate text-xs text-neutral-400">
                              {[notification.service, time].filter(Boolean).join(' · ')}
                            </p>
                            {!notification.read && (
                              <button
                                onClick={() => onMarkAsRead(notification.id)}
                                title="Marcar como leída"
                                aria-label={`Marcar como leída: ${notification.title}`}
                                className={`flex-shrink-0 rounded-lg p-1.5 text-cafe-600 transition-colors hover:bg-cafe-100/50 hover:text-cafe-800 ${FOCUS}`}
                              >
                                <Check className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </div>
      </aside>
    </div>
  );
};

export default NotificationsModal;
